import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CtaSection } from "../components/CtaSection";
import { usePageMeta } from "../hooks/usePageMeta";
import { CINEMATIC } from "../data/site";

const CATEGORIES = [
  {
    id: "hmo",
    eyebrow: "HMO & Licensing",
    title: "Houses in Multiple Occupation",
    items: [
      {
        q: "When does a property legally become an HMO?",
        a: "In England, a property is a House in Multiple Occupation when it is rented out to three or more people from more than one household who share facilities such as a kitchen or bathroom. The exact definition can vary slightly between councils, so always confirm locally.",
      },
      {
        q: "Do I always need a mandatory HMO licence?",
        a: "A mandatory HMO licence is required where the property is occupied by five or more people forming more than one household, regardless of the number of storeys. Smaller HMOs may still need a licence under additional licensing schemes operated by some councils.",
      },
      {
        q: "What is an Article 4 direction and why does it matter?",
        a: "An Article 4 direction removes the automatic right to change the use of a property to an HMO without planning permission. In Article 4 areas you'll need planning consent as well as any licence — this is one of the first things we check when sourcing an HMO.",
      },
      {
        q: "What's the difference between C4 and sui generis HMOs?",
        a: "A small HMO with three to six sharers usually falls within planning class C4. Larger HMOs with seven or more occupants are typically classed as sui generis and will need planning permission in their own right alongside licensing.",
      },
      {
        q: "What happens if I operate an unlicensed HMO?",
        a: "Operating an unlicensed HMO can lead to significant financial penalties, prosecution and rent repayment orders of up to 12 months of rent. Insurance and mortgage conditions can also be breached, so getting this right matters.",
      },
    ],
  },
  {
    id: "sdlt",
    eyebrow: "Stamp Duty",
    title: "Stamp Duty Land Tax",
    items: [
      {
        q: "What are the current SDLT rates for a main home?",
        a: "From 1 April 2025, residential rates in England are 0% up to £125,000, 2% between £125,001 and £250,000, 5% up to £925,000, 10% up to £1.5m and 12% above that. Each band is applied progressively — our calculator shows the band‑by‑band split.",
      },
      {
        q: "Who qualifies for first-time buyer relief?",
        a: "First-time buyer relief applies where every buyer has never owned a property anywhere in the world, the purchase price is £500,000 or less and the property will be used as the buyer's only or main residence. Pay 0% up to £300,000 and 5% between £300,001 and £500,000.",
      },
      {
        q: "What is the 5% additional property surcharge?",
        a: "If you'll own more than one residential property at completion — buy‑to‑lets, second homes, holiday homes — a 5% surcharge is added to every SDLT band. Our calculator shows the standard rate and the surcharge separately so you can see the impact.",
      },
      {
        q: "Can first-time buyer relief and the surcharge combine?",
        a: "No. If the purchase triggers the additional property surcharge, first-time buyer relief cannot apply — standard rates plus 5% on every band are used instead.",
      },
      {
        q: "Are there any extra charges for non-UK residents?",
        a: "Non-UK residents purchasing residential property in England usually pay an additional 2% surcharge on top of the standard rates and, where applicable, the additional property surcharge.",
      },
    ],
  },
  {
    id: "leasehold",
    eyebrow: "Leasehold & Tenancy",
    title: "Leasehold & Tenant Advice",
    items: [
      {
        q: "What is a leasehold and how long should a lease be?",
        a: "A leasehold means you own the right to live in a property for a fixed term — often 99, 125 or 999 years — under a lease agreement with a freeholder. As the remaining term reduces, so does the property's value; lenders typically want at least 70–85 years remaining at the end of a mortgage term.",
      },
      {
        q: "What is ground rent, and are the rules changing?",
        a: "Ground rent is a periodic payment to the freeholder. Recent reforms have reduced ground rent on most new leases to a peppercorn (effectively zero), but older leases can still carry doubling or escalating clauses that lenders don't like. We'll help you spot these early.",
      },
      {
        q: "What are service charges and can they be challenged?",
        a: "Service charges cover the cost of maintaining shared parts of a building. They must be reasonable and properly accounted for. If you believe they are unreasonable or the work hasn't been carried out, you can challenge them — ultimately through the First‑tier Tribunal.",
      },
      {
        q: "What should every tenant check before signing an AST?",
        a: "Confirm the deposit will be protected in an approved scheme, the right-to-rent checks have been done correctly, who the landlord is and their contact details, inventory and condition of the property, and whether the agent is a member of a redress scheme.",
      },
      {
        q: "Can I extend my lease?",
        a: "Most long leaseholders have a statutory right to extend their lease after two years of ownership. The process involves a valuation, a premium payable to the freeholder and a formal notice served under the 1993 Act. We can introduce specialist solicitors and valuers to run this for you.",
      },
    ],
  },
  {
    id: "working-with-us",
    eyebrow: "Working With Us",
    title: "Working With Elin's Consultancy",
    items: [
      {
        q: "How do you charge for your services?",
        a: "Fees depend on scope — a one-off consultation, ongoing sourcing, or a managed refurbishment project all look different. We'll always give you a clear written quote before any work starts, and never charge for an initial conversation.",
      },
      {
        q: "Do you promise returns on investment?",
        a: "No. We never promise returns — any claim like that in the UK property space is a red flag. What we offer is a careful process, honest guidance and the right professionals around you to make a well-informed decision.",
      },
      {
        q: "Where do you operate?",
        a: "We're based around London and the South East but our professional network extends across England. Confirm your target area with us and we'll let you know honestly whether we're the right fit.",
      },
      {
        q: "What does an initial consultation look like?",
        a: "A free 20–30 minute call to understand your situation and goals, with no obligation. If you'd like to continue, we'll propose a clear next step with scope, timeline and fee.",
      },
    ],
  },
];

const Accordion = ({ item, idx, catId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-forest/15 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`faq-item-${catId}-${idx}`}
        className="w-full flex items-start gap-6 py-6 text-left group"
      >
        <span className="w-8 h-8 shrink-0 mt-0.5 rounded-full border border-gold/70 flex items-center justify-center text-gold-dark transition-colors duration-300 group-hover:bg-gold group-hover:text-cream">
          {open ? <Minus size={14} strokeWidth={1.8} /> : <Plus size={14} strokeWidth={1.8} />}
        </span>
        <span className="flex-1 font-serif text-xl sm:text-2xl font-medium text-forest leading-[1.3] group-hover:text-ink transition-colors duration-300">
          {item.q}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0.0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-7 pl-14 pr-2 max-w-3xl">
              <p className="text-[14.5px] leading-[1.85] text-sage">{item.a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Faq = () => {
  usePageMeta(
    "FAQ | Elin's Consultancy",
    "Plain‑English answers to common questions on HMO licensing, Stamp Duty Land Tax, leasehold and working with Elin's Consultancy."
  );

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: CATEGORIES.flatMap((c) =>
        c.items.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        }))
      ),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", "faq-jsonld");
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <Layout>
      <PageHero
        eyebrow="Frequently Asked Questions"
        lines={[
          "Clear Answers,",
          <>
            <em className="italic font-normal text-gold-soft">Plain English</em>
          </>,
        ]}
        text="The questions we're asked most often — on HMO licensing, stamp duty, leasehold and how we work. Can't find your answer? Send us a message and we'll come back to you personally."
        image={CINEMATIC.archColumns}
        imageAlt="Classical fluted columns on a London period facade"
      />

      <section data-testid="faq-nav" className="bg-cream border-b border-forest/10">
        <div className="wrap-x py-6 flex flex-wrap gap-x-6 gap-y-3 items-center">
          <span className="text-[10.5px] tracking-[0.3em] uppercase font-bold text-sage">Jump to</span>
          {CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              data-testid={`faq-jump-${c.id}`}
              className="text-[12.5px] font-medium text-forest hover:text-gold-dark transition-colors duration-300 border-b border-transparent hover:border-gold"
            >
              {c.eyebrow}
            </a>
          ))}
        </div>
      </section>

      {CATEGORIES.map((cat, idx) => (
        <section
          key={cat.id}
          id={cat.id}
          data-testid={`faq-section-${cat.id}`}
          className={`${idx % 2 === 0 ? "bg-cream" : "bg-cream-2"} scroll-mt-24`}
        >
          <div className="wrap-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-[0.9fr_1.6fr] gap-10 lg:gap-16 items-start">
            <Reveal>
              <p className="eyebrow text-gold-dark">{cat.eyebrow}</p>
              <h2 className="font-serif text-4xl sm:text-[44px] font-medium text-forest leading-[1.08] mt-5">
                {cat.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-white border border-forest/10 px-6 sm:px-10">
                {cat.items.map((item, i) => (
                  <Accordion key={i} item={item} idx={i} catId={cat.id} />
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="bg-cream-3 border-t border-forest/10">
        <div className="wrap-x py-14 sm:py-16 text-center max-w-2xl mx-auto">
          <p className="eyebrow text-gold-dark">Didn't find your answer?</p>
          <h3 className="font-serif text-3xl font-medium text-forest mt-4 mb-5">
            Ask Us Directly
          </h3>
          <p className="text-[14px] leading-[1.8] text-sage">
            Property questions are rarely one‑size‑fits‑all. Send us a message with your situation
            and we'll come back with an honest, specific answer — usually within a working day.
          </p>
          <Link to="/contact" data-testid="faq-contact-cta" className="btn-forest mt-7">
            Send Us a Question <ArrowRight size={14} strokeWidth={2.2} />
          </Link>
        </div>
      </section>

      <CtaSection
        title={
          <>
            Ready for a
            <br />
            <em className="italic font-normal text-gold-soft">clearer answer?</em>
          </>
        }
        text="Book a 20‑minute consultation and get specific guidance on your property, licensing or leasehold situation."
      />
    </Layout>
  );
};

export default Faq;
